// client/src/components/admin/league-tabs/PendingInvitesTab.js
import React, { useState, useEffect, useCallback } from 'react'
import ApiRegistrations from '../../../api/ApiRegistrations'
import toastr from 'toastr'

/**
 * Admin tab showing pending doubles partner invites for a league.
 * Only rendered for Doubles leagues.
 */
function PendingInvitesTab({ league }) {
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [voidingId, setVoidingId] = useState(null)
  const [confirmVoidId, setConfirmVoidId] = useState(null)
  const [voidErrors, setVoidErrors] = useState({})

  const loadInvites = useCallback(async () => {
    if (!league?.id) return
    setLoading(true)
    setError(null)
    try {
      const data = await ApiRegistrations.getPendingInvites(league.id)
      setInvites(
        Array.isArray(data?.pending)
          ? data.pending
          : Array.isArray(data)
            ? data
            : [],
      )
    } catch (err) {
      setError('Failed to load pending invites.')
      console.error('PendingInvitesTab load error:', err)
    } finally {
      setLoading(false)
    }
  }, [league?.id])

  useEffect(() => {
    loadInvites()
  }, [loadInvites])

  const handleVoid = async (invite) => {
    setVoidingId(invite.registration_id)
    setVoidErrors((prev) => ({ ...prev, [invite.registration_id]: null }))
    try {
      await ApiRegistrations.voidPendingInvite({
        registration_id: invite.registration_id,
      })
      toastr.success('Registration voided.')
      setInvites((prev) =>
        prev.filter((i) => i.registration_id !== invite.registration_id),
      )
    } catch (err) {
      const msg = err?.message || 'Failed to void. Please try again.'
      setVoidErrors((prev) => ({ ...prev, [invite.registration_id]: msg }))
    } finally {
      setVoidingId(null)
      setConfirmVoidId(null)
    }
  }

  const formatDate = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div
          className="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span className="visually-hidden">Loading…</span>
        </div>
        <p className="mt-2 text-muted small">Loading pending invites…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={loadInvites}
        >
          Retry
        </button>
      </div>
    )
  }

  if (invites.length === 0) {
    return (
      <p className="text-muted" data-testid="pending-invites-empty">
        No pending partner invites for this league.
      </p>
    )
  }

  return (
    <div className="table-responsive" data-testid="pending-invites-table">
      <table className="table table-hover align-middle">
        <thead className="table-light">
          <tr>
            <th>Registrant</th>
            <th>Nominated Email</th>
            <th>Registered On</th>
            <th>Expires</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((invite) => (
            <tr key={invite.registration_id}>
              <td>
                {invite.registrant_name || `Player #${invite.registrant_id}`}
              </td>
              <td>{invite.nominated_partner_email}</td>
              <td>{formatDate(invite.created_at)}</td>
              <td>{formatDate(invite.expires_at)}</td>
              <td>
                {confirmVoidId === invite.registration_id ? (
                  <div
                    className="d-flex flex-column gap-1"
                    style={{ minWidth: 220 }}
                  >
                    <span className="small text-danger fw-semibold">
                      Void this registration?{' '}
                      {invite.registrant_name?.split(' ')[0] || 'This player'}{' '}
                      will be notified.
                    </span>
                    {voidErrors[invite.registration_id] && (
                      <span className="text-danger small">
                        {voidErrors[invite.registration_id]}
                      </span>
                    )}
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleVoid(invite)}
                        disabled={voidingId === invite.registration_id}
                        data-testid={`void-confirm-${invite.registration_id}`}
                      >
                        {voidingId === invite.registration_id ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            />
                            Voiding…
                          </>
                        ) : (
                          'Void'
                        )}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setConfirmVoidId(null)}
                        disabled={voidingId === invite.registration_id}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setConfirmVoidId(invite.registration_id)}
                    data-testid={`void-button-${invite.registration_id}`}
                  >
                    Void
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default PendingInvitesTab
